import type { EmailAddress, MailboxRole, Message, ThreadSummary } from '@wtfalch/email/mailbox';

import { DAY_MS, formatWhen, MONTHS, startOfDay, WEEKDAYS } from '../ui/format.ts';

/**
 * The pure pieces of the Mail section: turning a timestamp, a participant
 * list or a message count into the words and layout the board draws. Kept
 * apart from the components so they can be tested without a client, a DOM,
 * or a network. `formatWhen` itself lives in `ui/format.ts` -- Chat needs the
 * same ladder for its own rows and messages -- and is re-exported here so
 * Mail's own imports do not all have to change to reach it.
 */
export { formatWhen };

/** "started Monday": the weekday a thread's first message arrived, or a
 *  short date once that is more than a week ago. */
export function formatStarted(iso: string, now: Date = new Date()): string {
  const at = new Date(iso);
  const days = Math.floor((startOfDay(now) - startOfDay(at)) / DAY_MS);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days > 1 && days < 7) return WEEKDAYS[at.getDay()] ?? '';
  return `${at.getDate()} ${MONTHS[at.getMonth()] ?? ''}`;
}

export function displayName(address: EmailAddress): string {
  return address.name?.trim() || address.email;
}

/** "Saoirse Byrne, you and Marcus Yuen": every participant's display name
 *  (or address when there is none), the signed-in address replaced with
 *  "you", joined the way a sentence lists people rather than a comma-joined
 *  array. */
export function joinParticipants(participants: readonly EmailAddress[], meAddress: string): string {
  const me = meAddress.trim().toLowerCase();
  const names = participants.map((p) =>
    p.email.trim().toLowerCase() === me ? 'you' : displayName(p),
  );
  if (names.length === 0) return '';
  if (names.length === 1) return names[0] ?? '';
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** How many threads one request for the list asks for. */
export const LIST_PAGE_SIZE = 50;

/** A list row's names and avatar: "Saoirse Byrne, you" for everyone who
 *  wrote, the avatar the first of them who is not me. A thread only I wrote
 *  (Sent, Drafts) names who it went to instead, or every row there would
 *  read "you". */
export function rowPeople(
  thread: Pick<ThreadSummary, 'participants' | 'to' | 'from'>,
  meAddress: string,
): { names: string; avatar: EmailAddress } {
  const me = meAddress.trim().toLowerCase();
  const isMe = (a: EmailAddress) => a.email.trim().toLowerCase() === me;
  const written =
    thread.participants.length > 0 ? thread.participants : thread.from ? [thread.from] : [];
  const shown =
    written.length > 0 && written.every(isMe) && thread.to.length > 0 ? thread.to : written;
  return {
    names: shown.map((p) => (isMe(p) ? 'you' : displayName(p))).join(', ') || '(no sender)',
    avatar: shown.find((p) => !isMe(p)) ?? shown[0] ?? { email: '' },
  };
}

/** Which of a thread's messages render collapsed to one line -- every one
 *  but the newest, which always reads open. `showAll` reveals every earlier
 *  message; folded, only the one right before the newest does, so a long
 *  thread reads as "the last exchange, and how it got here" rather than a
 *  wall of quotes. */
export function collapsedIndices(total: number, showAll: boolean): number[] {
  if (total <= 1) return [];
  if (showAll) return Array.from({ length: total - 1 }, (_, i) => i);
  return [total - 2];
}

/** How many messages the "N earlier messages" toggle is hiding. Zero once
 *  it has been pressed, or when there is nothing to fold in the first
 *  place. */
export function hiddenMessageCount(total: number, showAll: boolean): number {
  if (showAll || total <= 2) return 0;
  return total - 2;
}

/** Everyone who wrote in a thread, oldest first, no duplicates -- the same
 *  rule `ThreadSummary.participants` follows, computed here for a
 *  `ThreadDetail`, which does not carry the field itself. */
export function participantsOf(messages: readonly Pick<Message, 'from'>[]): EmailAddress[] {
  const seen = new Set<string>();
  const out: EmailAddress[] = [];
  for (const message of messages) {
    for (const address of message.from) {
      const key = address.email.trim().toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(address);
    }
  }
  return out;
}

/** The composer's To/Cc/Bcc fields: a comma- or semicolon-separated line of
 *  addresses, each either bare (`ada@x.com`) or with a display name
 *  (`Ada <ada@x.com>`). A token that is not an address comes back as it was
 *  typed, so the caller can name it -- see `isAddress`. */
export function parseAddresses(raw: string): EmailAddress[] {
  return raw
    .split(/[,;]/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((token) => {
      const named = token.match(/^(.*?)\s*<([^<>]*)>$/);
      if (!named) return { email: token };
      const name = (named[1] ?? '').trim().replace(/^"(.*)"$/, '$1');
      return { ...(name ? { name } : {}), email: (named[2] ?? '').trim() };
    });
}

/** Whether `email` can go on an envelope: something, an `@`, something, no
 *  spaces. The server has the last word; this stops `priya` before it is sent. */
export function isAddress(email: string): boolean {
  return /^[^\s@<>]+@[^\s@<>]+$/.test(email);
}

/** A thread-row or collapsed-message snippet, stripped of Markdown link
 *  syntax -- a preview built from a signature or a quoted reply routinely
 *  carries a raw `[Proton Mail](https://proton.me/...)`, which a one-line
 *  snippet has no business rendering as source. Keeps the link text, drops
 *  the brackets and the URL; leaves everything else untouched, so a snippet
 *  with no Markdown in it passes through unchanged. */
export function stripMarkdownLinks(text: string): string {
  return text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
}

/** A message's body as paragraphs to draw one `<p>` each -- the plain-text
 *  body split on a blank line, or the list preview when there is no text
 *  body to split (an HTML-only message: Agora reads mail as text, so an
 *  HTML body is not rendered, only previewed). */
export function bodyParagraphs(message: Pick<Message, 'text' | 'preview'>): string[] {
  const text = message.text?.trim();
  if (text)
    return text
      .split(/\n{2,}/)
      .map((p) => p.trim())
      .filter(Boolean);
  return message.preview ? [message.preview] : [];
}

/** One run of a paragraph split on its own URLs: plain text, or a URL to
 *  draw as a real link. Pure and text-only -- a plain-text body is never
 *  parsed as HTML or Markdown here, only scanned for `http(s)://` runs, so
 *  a `<script>` or a `[text](url)` in the body reaches this as ordinary
 *  text, exactly as `bodyParagraphs` already returned it. */
export type BodySegment = { text: string } | { url: string };

const URL_PATTERN = /https?:\/\/[^\s<>()[\]{}"']+/g;
/** A URL a sentence runs straight into -- "...see https://x.com." -- keeps
 *  its trailing full stop, comma or the like out of the link; enclosing
 *  brackets and parens never reach the match at all, since `URL_PATTERN`
 *  already excludes them from a URL's own characters. */
const TRAILING_PUNCTUATION = /[.,!?:;]+$/;

export function linkify(text: string): BodySegment[] {
  const segments: BodySegment[] = [];
  let last = 0;
  for (const match of text.matchAll(URL_PATTERN)) {
    const start = match.index ?? 0;
    const trimmed = match[0].match(TRAILING_PUNCTUATION);
    const url = trimmed ? match[0].slice(0, -trimmed[0].length) : match[0];
    if (!url) continue;
    if (start > last) segments.push({ text: text.slice(last, start) });
    segments.push({ url });
    last = start + url.length;
  }
  if (last < text.length) segments.push({ text: text.slice(last) });
  return segments;
}

const ROLE_NAMES: Partial<Record<MailboxRole, string>> = {
  inbox: 'Inbox',
  sent: 'Sent',
  drafts: 'Drafts',
  archive: 'Archive',
  junk: 'Junk',
  trash: 'Trash',
};

/** A folder's name by what it is for, whatever the server calls it
 *  ("Sent Items", "Deleted Items"). Folders without a role, and roles with
 *  no short name here, keep the server's own. */
export function folderName(mailbox: { name: string; role: MailboxRole | null }): string {
  return (mailbox.role && ROLE_NAMES[mailbox.role]) || mailbox.name;
}

export type SnoozeOption = {
  id: 'later' | 'tomorrow' | 'weekend' | 'next-week';
  label: string;
  at: Date;
};

/** The times the toolbar's Snooze offers, in the person's own time zone:
 *  this evening (only while it is still ahead), tomorrow morning, Saturday
 *  morning (only on a weekday) and Monday morning. */
export function snoozeOptions(now: Date = new Date()): SnoozeOption[] {
  const at = (days: number, hour: number) =>
    new Date(now.getFullYear(), now.getMonth(), now.getDate() + days, hour, 0, 0, 0);
  const day = now.getDay();
  const options: SnoozeOption[] = [];
  if (now.getHours() < 16) options.push({ id: 'later', label: 'This evening', at: at(0, 18) });
  options.push({ id: 'tomorrow', label: 'Tomorrow morning', at: at(1, 8) });
  // On a Friday, Saturday morning already is "Tomorrow morning" (agora#484)
  // -- a weekend option naming the same moment is not a second choice.
  if (day >= 1 && day <= 4)
    options.push({ id: 'weekend', label: 'This weekend', at: at(6 - day, 8) });
  options.push({ id: 'next-week', label: 'Next week', at: at((8 - day) % 7 || 7, 8) });
  return options;
}

const LABEL_MAX_LENGTH = 64;
// A label is a JMAP keyword, carried as an IMAP flag underneath -- IMAP's
// own atom grammar (RFC 3501) refuses whitespace, control characters and
// "(", ")", "{", "%", "*", DQUOTE, "\" and "]" in an atom. The server
// refuses these too (agora#484); catching them here means the toolbar can
// say which character is the problem instead of the add round-tripping to
// a refusal.
const LABEL_SPECIAL_CHARS = /[\s()[\]{}%*"\\]/;

function isControlChar(char: string): boolean {
  const code = char.codePointAt(0) ?? 0;
  return code <= 0x1f || code === 0x7f;
}

/** Why `name` cannot be added as a label, or `null` when it can. */
export function labelProblem(name: string): string | null {
  if (!name) return 'Type a label.';
  if (name.length > LABEL_MAX_LENGTH)
    return `A label cannot be longer than ${LABEL_MAX_LENGTH} characters.`;
  if ([...name].some(isControlChar)) return 'A label cannot contain a control character.';
  const bad = LABEL_SPECIAL_CHARS.exec(name)?.[0];
  if (bad) return `A label cannot contain ${JSON.stringify(bad)}.`;
  return null;
}
